import undoable, { includeAction, newHistory, type StateWithHistory } from 'redux-undo'

import { ActionTypes } from './constants'
import { syncLinkedPerimeters } from './linkedPerimeters'
import type { EditProjectActions, EditProjectState } from './types'

const init: EditProjectState = {
  scale: {},
  measure: [],
  linearLines: [],
  currentPoints: [],
  volumes: [],
  counts: [],
  isEdited: false,
  comments: [],
  freeHandShapes: [],
  penShapes: [],
  cloudShapes: [],
  textShapes: [],
  polygons: [],
  editedPage: {
    id: '',
    pageId: '',
    name: '',
    sectionName: '',
  },
}

const edit = (state = init, action: EditProjectActions): EditProjectState => {
  switch (action.type) {
    //check past and future

    case ActionTypes.CLEAN_HISTORY: {
      return {
        ...state,
      }
    }

    case ActionTypes.UNDO: {
      return {
        ...state,
        isEdited: true,
      }
    }

    case ActionTypes.SET_EDITED_PAGE: {
      return {
        ...state,
        isEdited: true,
      }
    }

    case ActionTypes.UPDATE_COMMENTS: {
      return {
        ...state,
        comments: action.payload.comments,
        isEdited: true,
      }
    }

    case ActionTypes.UPDATE_LINEAR_LINES: {
      return {
        ...state,
        linearLines: syncLinkedPerimeters(action.payload.linearLines, state.polygons),
        isEdited: true,
      }
    }

    case ActionTypes.UPDATE_FREE_HAND_SHAPES: {
      return {
        ...state,
        freeHandShapes: action.payload.freeHandShapes,
        isEdited: true,
      }
    }

    case ActionTypes.UPDATE_PEN_SHAPES: {
      return {
        ...state,
        penShapes: action.payload.penShapes,
        isEdited: true,
      }
    }
    case ActionTypes.UPDATE_CLOUD_SHAPES: {
      return {
        ...state,
        cloudShapes: action.payload.cloudShapes,
        isEdited: true,
      }
    }
    case ActionTypes.UPDATE_TEXT_SHAPES: {
      return {
        ...state,
        textShapes: action.payload.textShapes,
        isEdited: true,
      }
    }

    case ActionTypes.UPDATE_POLYGONS:
      console.log('Updating polygons with payload:', action.payload)
      return {
        ...state,
        polygons: action.payload.polygons,
        linearLines: syncLinkedPerimeters(state.linearLines, action.payload.polygons),
        isEdited: true,
      }

    case ActionTypes.UPDATE_COUNTS: {
      return {
        ...state,
        counts: action.payload.counts,
        isEdited: true,
      }
    }

    case ActionTypes.PROJECT_UPDATED: {
      // Save echoes and remote updates arrive mid-drawing; they must not drop the pending points.
      return {
        ...state,
        linearLines: action.payload.linearLines,
        counts: action.payload.counts,
        polygons: action.payload.polygons,

        comments: action.payload.comments,
        isEdited: false,
        scale: action.payload.scale,
      }
    }
    case ActionTypes.ADD_TO_CURRENT_POINTS: {
      return {
        ...state,
        currentPoints: [...(state.currentPoints ? state.currentPoints : []), action.payload.point],
        isEdited: true,
      }
    }

    case ActionTypes.SET_CURRENT_POINTS: {
      return {
        ...state,
        currentPoints: (action.payload as { points: EditProjectState['currentPoints'] }).points,
        isEdited: true,
      }
    }

    case ActionTypes.REMOVE_LAST_CURRENT_POINT: {
      return {
        ...state,
        currentPoints: (state.currentPoints ?? []).slice(0, -1),
        isEdited: true,
      }
    }

    case ActionTypes.CLEAR_CURRENT_POINTS: {
      return {
        ...state,
        currentPoints: [],
        isEdited: true,
      }
    }

    case ActionTypes.SET_SCALE: {
      return {
        ...state,
        scale: {
          ...action.payload.pageScale,
          measureSystem: action.payload.pageScale?.measureSystem,
        },
        isEdited: true,
      }
    }

    case ActionTypes.ADD_MEASURE_LINE: {
      return {
        ...state,
        measure: [...(state.measure ? state.measure : []), action.payload.measureLine],
        isEdited: true,
      }
    }

    case ActionTypes.CHANGE_MEASURE_LINE: {
      return {
        ...state,
        measure: state.measure!.map((m) =>
          m.id === action.payload.measureLine.id
            ? {
                ...m,
                firstPoint: action.payload.measureLine.firstPoint,
                secondPoint: action.payload.measureLine.secondPoint,
                length: action.payload.measureLine.length,
                width: action.payload.measureLine.width,
                isCompleted: action.payload.measureLine.isCompleted,
              }
            : m,
        ),
        isEdited: true,
      }
    }

    case ActionTypes.REMOVE_MEASURE_LINE: {
      return {
        ...state,
        measure: state.measure!.filter((m) => m.id !== action.payload.id),
        isEdited: true,
      }
    }

    case ActionTypes.UPDATE_MEASURES: {
      return {
        ...state,
        measure: action.payload.measures ? action.payload.measures : state.measure,
        isEdited: true,
      }
    }

    case ActionTypes.SET_CURRENT_EDIT: {
      return action.payload.currentEdit
    }

    case ActionTypes.BATCH_DELETE_SHAPES: {
  const polygons = action.payload.polygons ?? state.polygons
  return {
    ...state,
    polygons,
    linearLines: syncLinkedPerimeters(action.payload.linearLines ?? state.linearLines, polygons),
    counts: action.payload.counts ?? state.counts,
    measure: action.payload.measures ?? state.measure,
    isEdited: true,
  }
}

    //refactor Required

    default:
      return state
  }
}

const undoableEdit = undoable(edit, {
  filter: includeAction([
    ActionTypes.ADD_MEASURE_LINE,
    ActionTypes.CHANGE_MEASURE_LINE,
    ActionTypes.REMOVE_MEASURE_LINE,
    ActionTypes.SET_SCALE,
    //ActionTypes.ADD_TO_CURRENT_POINTS,
    ActionTypes.UPDATE_LINEAR_LINES,
    ActionTypes.UPDATE_POLYGONS,
    ActionTypes.UPDATE_COUNTS,
    ActionTypes.ADD_TO_CURRENT_POINTS,
    ActionTypes.REMOVE_LAST_CURRENT_POINT,

    ActionTypes.UPDATE_FREE_HAND_SHAPES,
    ActionTypes.UPDATE_PEN_SHAPES,
    ActionTypes.UPDATE_CLOUD_SHAPES,
    ActionTypes.UPDATE_TEXT_SHAPES,
      ActionTypes.BATCH_DELETE_SHAPES,

  ]),
  //clearHistoryType: ActionTypes.CLEAN_HISTORY,
  ignoreInitialState: true,
  syncFilter: true,
  undoType: ActionTypes.UNDO,
  neverSkipReducer: true,
  //initTypes: ['@@redux/INIT', '@@INIT'], // Ensure initial state setup
})

// Loading another page must drop history; otherwise undo restores the previous page's snapshot (and saves it).
const editWithPageReset = (state: StateWithHistory<EditProjectState> | undefined, action: EditProjectActions) => {
  const next = undoableEdit(state, action)
  if (
    action.type !== ActionTypes.SET_CURRENT_EDIT ||
    state?.present.editedPage?.pageId === next.present.editedPage?.pageId
  ) {
    return next
  }
  return newHistory([], next.present, [])
}

export default editWithPageReset
